import { autoinject } from 'aurelia-framework';
import { NetlogHttpClient } from 'lib/http';
import { QueryFilter } from 'lib/tables/DataFilter';
import { CalculateUtilizationFactorDto, MooringStep } from 'models/mooring/UtilizationFactor';
import {
  MooringAnchorLine,
  MooringBridle,
  MooringBuoy,
  MooringGridLine,
  UpdateMooringAnchorLineDto,
  UpdateMooringBridleDto,
  UpdateMooringBuoyDto,
  UpdateMooringGridLineDto,
  MooringComponentCommentDto,
  CommentForEntity,
  CreateComponentCommentDto,
  UpdateComponentCommentDto,
  ResolveComponentCommentDto,
  AddMooringAnchorLineDto,
  AddMooringGridLineDto,
  AddMooringBuoyDto,
  AddMooringBridleDto,
  MooringDrawingSetup,
  MooringElementsListDto,
  MooringProductionAnchorLineDto,
  UpdateMooringProductionAnchorLineDto,
  UpdateMooringFleetAnchorLineDto,
  MooringFleetAnchorLine,
  MooringProductionFleetAnchorLineDto,
  UpdateMooringProductionFleetAnchorLineDto,
  MooringProductionGridLineDto,
  MooringProductionGridLineEditDto,
  MooringProductionBuoyDto,
  MooringProductionBuoyEditDto,
  MooringProductionBridleDto,
  MooringProductionBridleEditDto,
  MooringCage,
  AddMooringCageDto,
  UpdateMooringCageDto,
  MooringDto,
} from 'models/mooring';

@autoinject
export class MooringService {
  constructor(private client: NetlogHttpClient) {}

  async getMooring(id: number) {
    return await this.client.get<MooringDto>({
      url: `api/moorings/${id}`,
      model: MooringDto,
    });
  }

  async exportProductionExcelFile(mooringId: number) {
    return await this.client.downloadBlob(`api/moorings/${mooringId}/production-excel`);
  }

  async getMooringDrawingSetup(id: number) {
    return await this.client.get<MooringDrawingSetup>({
      url: `api/moorings/${id}/drawing-setup`,
      model: MooringDrawingSetup,
    });
  }

  async getMooringElementsList(id: number) {
    return await this.client.get<MooringElementsListDto>({
      url: `api/moorings/${id}/elements-list`,
      model: MooringElementsListDto,
    });
  }
  // FLEET ANCHOR LINES
  async getFleetAnchorLines(mooringId: number) {
    return await this.client.get<MooringFleetAnchorLine[]>({
      url: `api/moorings/${mooringId}/fleet-anchor-lines`,
      model: MooringFleetAnchorLine,
    });
  }

  async updateFleetAnchorLines(mooringId: number, dto: UpdateMooringFleetAnchorLineDto[]) {
    return await this.client.patch({
      url: `api/moorings/${mooringId}/fleet-anchor-lines`,
      data: dto,
    });
  }

  async deleteFleetAnchorLine(mooringId: number, anchorLineId: number) {
    return await this.client.delete({
      url: `api/moorings/${mooringId}/fleet-anchor-lines/${anchorLineId}`,
    });
  }

  // FLEET ANCHOR LINES — PRODUCTION
  async getProductionFleetAnchorLines(mooringId: number) {
    return await this.client.get<MooringProductionFleetAnchorLineDto[]>({
      url: `api/moorings/${mooringId}/production-fleet-anchor-lines`,
      model: MooringProductionFleetAnchorLineDto,
    });
  }

  async updateProductionFleetAnchorLines(mooringId: number, dto: UpdateMooringProductionFleetAnchorLineDto[]) {
    return await this.client.patch({
      url: `api/moorings/${mooringId}/production-fleet-anchor-lines`,
      data: dto,
    });
  }

  // ANCHOR LINES
  async getProductionAnchorLines(mooringId: number) {
    return await this.client.get<MooringProductionAnchorLineDto[]>({
      url: `api/moorings/${mooringId}/production-anchor-lines`,
      model: MooringProductionAnchorLineDto,
    });
  }

  async exportProductionAnchorLines(mooringId: number) {
    return await this.client.downloadBlob(`api/moorings/${mooringId}/production-anchor-lines/export`);
  }

  async exportProductionFleetAnchorLines(mooringId: number) {
    return await this.client.downloadBlob(`api/moorings/${mooringId}/production-fleet-anchor-lines/export`);
  }

  async updateProductionAnchorLines(mooringId: number, dto: UpdateMooringProductionAnchorLineDto[]) {
    return await this.client.patch({
      url: `api/moorings/${mooringId}/production-anchor-lines`,
      data: dto,
    });
  }

  async getAnchorLines(mooringId: number) {
    return await this.client.get<MooringAnchorLine[]>({
      url: `api/moorings/${mooringId}/anchor-lines`,
      model: MooringAnchorLine,
    });
  }

  async addAnchorLine(mooringId: number, dto: AddMooringAnchorLineDto[]) {
    return await this.client.post({
      url: `api/moorings/${mooringId}/anchor-lines`,
      data: dto,
    });
  }

  async deleteAnchorLine(mooringId: number, anchorLineId: number) {
    return await this.client.delete({
      url: `api/moorings/${mooringId}/anchor-lines/${anchorLineId}`,
    });
  }

  async updateAnchorLines(mooringId: number, dto: UpdateMooringAnchorLineDto[]) {
    return await this.client.patch({
      url: `api/moorings/${mooringId}/anchor-lines`,
      data: dto,
    });
  }

  // GRID LINES
  async getGridLines(mooringId: number, queryFilter?: QueryFilter) {
    return await this.client.get<MooringGridLine[]>({
      url: `api/moorings/${mooringId}/grid-lines${queryFilter?.asQueryString || ''}`,
      model: MooringGridLine,
    });
  }

  async exportProductionGridLines(mooringId: number) {
    return await this.client.downloadBlob(`api/moorings/${mooringId}/production-grid-lines/export`);
  }

  async getProductionGridLines(mooringId: number) {
    return await this.client.get<MooringProductionGridLineDto[]>({
      url: `api/moorings/${mooringId}/production-grid-lines`,
      model: MooringProductionGridLineDto,
    });
  }

  async updateProductionGridLines(mooringId: number, dto: MooringProductionGridLineEditDto[]) {
    return await this.client.patch({
      url: `api/moorings/${mooringId}/production-grid-lines`,
      data: dto,
    });
  }

  async addGridLine(mooringId: number, dto: AddMooringGridLineDto[]) {
    return await this.client.post({
      url: `api/moorings/${mooringId}/grid-lines`,
      data: dto,
    });
  }

  async deleteGridLines(mooringId: number, gridLineId: number) {
    return await this.client.delete({
      url: `api/moorings/${mooringId}/grid-lines/${gridLineId}`,
    });
  }

  async updateGridLines(mooringId: number, dto: UpdateMooringGridLineDto[]) {
    return await this.client.patch({
      url: `api/moorings/${mooringId}/grid-lines`,
      data: dto,
    });
  }

  // BUOYS
  async getBuoys(mooringId: number, queryFilter?: QueryFilter) {
    return await this.client.get<MooringBuoy[]>({
      url: `api/moorings/${mooringId}/buoys${queryFilter?.asQueryString || ''}`,
      model: MooringBuoy,
    });
  }

  async exportProductionBuoys(mooringId: number) {
    return await this.client.downloadBlob(`api/moorings/${mooringId}/production-buoys/export`);
  }

  async getProductionBuoys(mooringId: number) {
    return await this.client.get<MooringProductionBuoyDto[]>({
      url: `api/moorings/${mooringId}/production-buoys`,
      model: MooringProductionBuoyDto,
    });
  }

  async updateProductionBuoys(mooringId: number, dto: MooringProductionBuoyEditDto[]) {
    return await this.client.patch({
      url: `api/moorings/${mooringId}/production-buoys`,
      data: dto,
    });
  }

  async addBuoy(mooringId: number, dto: AddMooringBuoyDto[]) {
    return await this.client.post({
      url: `api/moorings/${mooringId}/buoys`,
      data: dto,
    });
  }

  async deleteBuoy(mooringId: number, buoyId: number) {
    return await this.client.delete({
      url: `api/moorings/${mooringId}/buoys/${buoyId}`,
    });
  }

  async updateBuoys(mooringId: number, dto: UpdateMooringBuoyDto[]) {
    return await this.client.patch({
      url: `api/moorings/${mooringId}/buoys`,
      data: dto,
    });
  }

  // BRIDLES
  async getBridles(mooringId: number, queryFilter?: QueryFilter) {
    return await this.client.get<MooringBridle[]>({
      url: `api/moorings/${mooringId}/bridles${queryFilter?.asQueryString || ''}`,
      model: MooringBridle,
    });
  }

  async exportProductionBridles(mooringId: number) {
    return await this.client.downloadBlob(`api/moorings/${mooringId}/production-bridles/export`);
  }

  async getProductionBridles(mooringId: number) {
    return await this.client.get<MooringProductionBridleDto[]>({
      url: `api/moorings/${mooringId}/production-bridles`,
      model: MooringProductionBridleDto,
    });
  }

  async updateProductionBridles(mooringId: number, dto: MooringProductionBridleEditDto[]) {
    return await this.client.patch({
      url: `api/moorings/${mooringId}/production-bridles`,
      data: dto,
    });
  }

  async addBridle(mooringId: number, dto: AddMooringBridleDto[]) {
    return await this.client.post({
      url: `api/moorings/${mooringId}/bridles`,
      data: dto,
    });
  }

  async deleteBridle(mooringId: number, bridleId: number) {
    return await this.client.delete({
      url: `api/moorings/${mooringId}/bridles/${bridleId}`,
    });
  }

  async updateBridles(mooringId: number, dto: UpdateMooringBridleDto[]) {
    return await this.client.patch({
      url: `api/moorings/${mooringId}/bridles`,
      data: dto,
    });
  }

  // CAGES
  async getCages(mooringId: number) {
    return await this.client.get<MooringCage[]>({
      url: `api/moorings/${mooringId}/cages`,
      model: MooringCage,
    });
  }

  async addCage(mooringId: number, dto: AddMooringCageDto[]) {
    return await this.client.post({
      url: `api/moorings/${mooringId}/cages`,
      data: dto,
    });
  }

  async deleteCage(mooringId: number, cageId: number) {
    return await this.client.delete({
      url: `api/moorings/${mooringId}/cages/${cageId}`,
    });
  }

  async updateCages(mooringId: number, dto: UpdateMooringCageDto[]) {
    return await this.client.patch({
      url: `api/moorings/${mooringId}/cages`,
      data: dto,
    });
  }

  // Utilization Factor
  async calculateUtilizationFactor({
    mooringId,
    mooringStep,
    dto,
  }: {
    mooringId: number;
    mooringStep: MooringStep;
    dto: CalculateUtilizationFactorDto[];
  }) {
    return await this.client.patch<CalculateUtilizationFactorDto[]>({
      url: `api/moorings/${mooringId}/components/${mooringStep}/utilization-factor`,
      data: dto,
      model: CalculateUtilizationFactorDto,
    });
  }

  // Comments

  async getMooringComponentComments({ mooringId }: { mooringId: number }) {
    return await this.client.get<MooringComponentCommentDto[]>({
      url: `api/moorings/${mooringId}/components/comments`,
      model: MooringComponentCommentDto,
    });
  }

  async getComponentComments({ mooringId, entity, componentId }: { mooringId: number; componentId: number; entity: CommentForEntity }) {
    const params = new URLSearchParams();
    params.set('entity', entity.toString());

    return await this.client.get<MooringComponentCommentDto[]>({
      url: `api/moorings/${mooringId}/components/${componentId}/comments?${params.toString()}`,
      model: MooringComponentCommentDto,
    });
  }

  async createComponentComment({ mooringId, componentId, dto }: { mooringId: number; componentId: number; dto: CreateComponentCommentDto }) {
    return await this.client.post({
      url: `api/moorings/${mooringId}/components/${componentId}/comments`,
      data: dto,
    });
  }

  async resolveComponentComment({ mooringId, commentId, dto }: { mooringId: number; commentId: number; dto: ResolveComponentCommentDto }) {
    return await this.client.post({
      url: `api/moorings/${mooringId}/components/comments/${commentId}/resolve`,
      data: dto,
    });
  }

  async deleteComponentComment({ mooringId, commentId }: { mooringId: number; commentId: number }) {
    return await this.client.delete({
      url: `api/moorings/${mooringId}/components/comments/${commentId}`,
    });
  }

  async updateComponentComment({ mooringId, commentId, dto }: { mooringId: number; commentId: number; dto: UpdateComponentCommentDto }) {
    return await this.client.patch({
      url: `api/moorings/${mooringId}/components/comments/${commentId}`,
      data: dto,
    });
  }

  updateOffer() {}
}
